/**
 * Standard 6-button time-period segment control (Day / Week / MTD / QTD /
 * YTD / LTD). Reads/writes the `?period=` URL search param so deep links
 * carry the user's selection.
 *
 * Per dashboard standards (PR 1 of 6): every dashboard / KPI tile / chart
 * accepts a period. Each page picks its own default — Overview = M, Hub = D —
 * via the `defaultPeriod` prop. If the URL already has a `period` param,
 * that wins; otherwise the default is written into the URL on mount so
 * downstream hooks see a stable value.
 */

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { PERIODS, PERIOD_LABELS, parsePeriod, type Period } from '@/lib/timePeriod';
import { cn } from '@/lib/utils';

export interface TimePeriodSelectorProps {
  /** Period selected when the URL has no `period` param. */
  defaultPeriod?: Period;
  /** Optional className applied to the wrapping <div> for layout overrides. */
  className?: string;
}

export function TimePeriodSelector({
  defaultPeriod = 'M',
  className,
}: TimePeriodSelectorProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('period');
  const current: Period = parsePeriod(raw, defaultPeriod);

  // Seed the URL on mount if no param is present, so downstream consumers
  // (KPI hooks, charts) read a deterministic value instead of "undefined".
  useEffect(() => {
    if (raw === null) {
      const next = new URLSearchParams(searchParams);
      next.set('period', defaultPeriod);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (period: Period) => {
    const next = new URLSearchParams(searchParams);
    next.set('period', period);
    setSearchParams(next, { replace: false });
  };

  return (
    <div
      className={cn('inline-flex items-center gap-1 rounded-md border bg-background p-1', className)}
      role="group"
      aria-label="Time period selector"
    >
      {PERIODS.map((period) => (
        <Button
          key={period}
          size="sm"
          variant={period === current ? 'default' : 'outline'}
          onClick={() => handleSelect(period)}
          aria-pressed={period === current}
        >
          {PERIOD_LABELS[period]}
        </Button>
      ))}
    </div>
  );
}
